// src/components/signup/RegistrationForm.js
// *** UPDATED: Uses atomic signup endpoint for email/password users ***

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../backend/supabase';
import axiosInstance from '../../axiosInstance'; // *** Ensure Axios is imported ***
import { PersonalDetails } from './registration/RegistrationPhases/PersonalDetails';
import { FirmDetails } from './registration/RegistrationPhases/FirmDetails';
import { BarVerification } from './registration/RegistrationPhases/BarVerification';
import { PhaseIndicator } from './registration/PhaseIndicator';
// Keep checkUserExistsByEmail ONLY if doing non-OAuth signup AND you want the extra frontend check
// import { checkUserExistsByEmail } from '../../backend/userService';
import { Loader2 } from 'lucide-react'; // Import Loader icon

const RegistrationForm = () => {
    const navigate = useNavigate();
    const [currentPhase, setCurrentPhase] = useState(1); // Default to phase 1
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isOAuthUser, setIsOAuthUser] = useState(false); // Track flow type
    // formData state holds data from all phases
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', password: '', confirmPassword: '', workEmail: '',
        secondaryEmail: '', phone: '', secondaryPhone: '', firmName: '',
        office_address: '', firmCity: '', firmState: '', firmZip: '',
        practiceAreas: [], barId: '', yearsOfExperience: '',
        publicReferrals: false, bio: '', website: '',
    });
    const submittedRef = useRef(false);

    // Initialization useEffect (handling OAuth resume)
    // *** MODIFIED THIS useEffect's initializeRegistration function ***
    useEffect(() => {
        const initializeRegistration = async () => {
            const userRole = localStorage.getItem('userRole');
            // Redirect if not an attorney trying to register (can be refined based on exact flow)
            if (!userRole || userRole !== 'attorney') {
                // Only redirect if trying to access registration steps directly without role
                if (window.location.pathname === '/signup/register' || window.location.pathname === '/signup/complete-profile'){
                     navigate('/signup'); // Redirect to initial role selection
                     return;
                }
            }

            const tempAuthUserJSON = localStorage.getItem('tempAuthUser');

            if (tempAuthUserJSON) {
                try {
                    const authUserDataFromStorage = JSON.parse(tempAuthUserJSON);

                    // For OAuth users, the session should already be active from AuthCallback
                    // We primarily use tempAuthUser for pre-filling data that AuthCallback stored.
                    const { data: { session } } = await supabase.auth.getSession();

                    if (authUserDataFromStorage && session?.user?.email === authUserDataFromStorage.email) {
                        let fName = '';
                        let lName = '';
                        const meta = authUserDataFromStorage.user_metadata || {}; // Ensure meta is an object

                        // Prioritize given_name and family_name if they exist (common in some OAuth providers)
                        fName = meta.given_name || meta.first_name || '';
                        lName = meta.family_name || meta.last_name || '';

                        // If still no first name, try parsing full_name or name
                        if (!fName && (meta.full_name || meta.name)) {
                            const nameToParse = meta.full_name || meta.name;
                            const nameParts = nameToParse.trim().split(' ');
                            fName = nameParts[0] || '';
                            if (nameParts.length > 1) {
                                lName = nameParts.slice(1).join(' ').trim() || '';
                            }
                        }
                        // Final fallback if still empty
                        if (!fName && meta.name) fName = meta.name;


                        const email = authUserDataFromStorage.email || session?.user?.email || '';

                        setFormData(prevData => ({
                            ...prevData,
                            firstName: fName,
                            lastName: lName,
                            workEmail: email // Email is pre-filled from OAuth provider
                        }));
                        setIsOAuthUser(true);
                        // For OAuth users, skip password phase (Phase 1).
                        // Start them at Phase 2 (Contact Info) or Phase 3 (Firm Details) if Phase 2 is also pre-filled.
                        // Assuming Phase 1 = Name/Pass, Phase 2 = Email/Phone, Phase 3 = Firm/Bar
                        setCurrentPhase(2); // Start at contact details or adjust as needed
                        // Do NOT remove tempAuthUser here; remove it after successful profile submission.
                    } else {
                        localStorage.removeItem('tempAuthUser');
                        setIsOAuthUser(false);
                        setCurrentPhase(1); // Default to fresh email/pass signup
                    }
                } catch (error) {
                    localStorage.removeItem('tempAuthUser'); // Clear on error
                    setIsOAuthUser(false);
                    setCurrentPhase(1);
                }
            } else {
                setIsOAuthUser(false);
                setCurrentPhase(1);
            }
        };
        initializeRegistration();
    }, [navigate]); // navigate is a dependency

    // Validation function (Keep as is - NO CHANGES)
    const validatePhase = (phase) => {
         const validationErrors = {}; const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); const isValidPhone = (phone) => /^\+?\d{10,15}$/.test(phone?.replace(/\D/g, '')); const isValidZipCode = (zip) => /^\d{5}(-\d{4})?$/.test(zip);
         switch (phase) {
             case 1: if (!formData.firstName.trim()) validationErrors.firstName = 'First name required'; if (!formData.lastName.trim()) validationErrors.lastName = 'Last name required'; if (!isOAuthUser) { if (!formData.password) validationErrors.password = 'Password required'; else if (formData.password.length < 8) validationErrors.password = 'Password must be 8+ characters'; if (formData.password !== formData.confirmPassword) validationErrors.confirmPassword = 'Passwords do not match'; } break;
             case 2: if (!isOAuthUser) { if (!formData.workEmail) validationErrors.workEmail = 'Work email required'; else if (!isValidEmail(formData.workEmail)) validationErrors.workEmail = 'Invalid work email';} if (formData.secondaryEmail && !isValidEmail(formData.secondaryEmail)) validationErrors.secondaryEmail = 'Invalid secondary email'; if (!formData.phone) validationErrors.phone = 'Phone required'; else if (!isValidPhone(formData.phone)) validationErrors.phone = 'Invalid phone (+1...)'; if (formData.secondaryPhone && !isValidPhone(formData.secondaryPhone)) validationErrors.secondaryPhone = 'Invalid secondary phone'; break;
             case 3: if (!formData.firmName.trim()) validationErrors.firmName = 'Firm name required'; if (!formData.office_address.trim()) validationErrors.office_address = 'Firm address required'; if (!formData.barId.trim()) validationErrors.barId = 'Bar ID required'; if (!formData.firmCity.trim()) validationErrors.firmCity = 'City required'; if (!formData.firmState) validationErrors.firmState = 'State required'; if (!formData.firmZip) validationErrors.firmZip = 'ZIP required'; else if (!isValidZipCode(formData.firmZip)) validationErrors.firmZip = 'Invalid ZIP'; if (formData.yearsOfExperience === '' || isNaN(parseInt(formData.yearsOfExperience)) || parseInt(formData.yearsOfExperience) < 0) { validationErrors.yearsOfExperience = 'Years of experience required (0+)'; } if (!formData.practiceAreas || formData.practiceAreas.length === 0) { validationErrors.practiceAreas = 'Select at least one practice area'; } break;
             default: break;
         } return validationErrors;
     };

    // Handle Back Navigation (Keep as is - NO CHANGES)
    const handleBack = () => { if (loading) return; if (currentPhase > 1) { if (isOAuthUser && currentPhase === 2) { localStorage.removeItem('tempAuthUser'); localStorage.removeItem('userRole'); navigate('/signup'); } else { setCurrentPhase(prev => prev - 1); } } else { localStorage.removeItem('tempAuthUser'); localStorage.removeItem('userRole'); navigate(-1); } };

    // Handle Next Step Navigation (UPDATED - removed auth signup)
    const handleNext = async () => {
        if (loading) return;
        const validationErrors = validatePhase(currentPhase);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        
        // For email/password users, just move through phases without creating account
        if (currentPhase < 3) {
            setCurrentPhase(prev => prev + 1);
        }
        // Don't create account until phase 3 (all data collected)
    };

    // Handle General Input Changes (Keep as is - NO CHANGES)
    const handleChange = (e) => { const { name, value, type, checked } = e.target; if (errors[name]) { setErrors(prev => ({ ...prev, [name]: undefined })); } if (name === 'practiceAreas') { const areaValue = value; setFormData(prev => { const currentAreas = prev.practiceAreas || []; const updatedAreas = checked ? [...currentAreas, areaValue] : currentAreas.filter(area => area !== areaValue); return { ...prev, practiceAreas: updatedAreas }; }); } else { setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value })); } };

    // handleSubmit (UPDATED - uses atomic signup for email users)
    const handleSubmit = async () => {
        if (submittedRef.current || loading) return;

        const validationErrors = validatePhase(3);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        setLoading(true);
        submittedRef.current = true;

        try {
            // For OAuth users, use the existing complete-profile endpoint
            if (isOAuthUser) {
                const profileData = {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    workEmail: formData.workEmail,
                    phone: formData.phone,
                    firmName: formData.firmName,
                    office_address: formData.office_address,
                    firmCity: formData.firmCity,
                    firmState: formData.firmState,
                    firmZip: formData.firmZip,
                    practiceAreas: formData.practiceAreas,
                    barId: formData.barId,
                    yearsOfExperience: formData.yearsOfExperience,
                    publicReferrals: formData.publicReferrals,
                    bio: formData.bio,
                    website: formData.website,
                };

                const response = await axiosInstance.post('/api/users/complete-profile', profileData);
                
                if (response.data.success) {
                    localStorage.removeItem('tempAuthUser');
                    navigate('/pending-approval');
                } else {
                    throw new Error(response.data.error || "Failed to complete profile");
                }
            } else {
                // For email/password users, use the new atomic signup endpoint
                const signupData = {
                    // All data from all phases
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    password: formData.password,
                    workEmail: formData.workEmail,
                    phone: formData.phone,
                    secondaryEmail: formData.secondaryEmail,
                    secondaryPhone: formData.secondaryPhone,
                    firmName: formData.firmName,
                    office_address: formData.office_address,
                    firmCity: formData.firmCity,
                    firmState: formData.firmState,
                    firmZip: formData.firmZip,
                    barId: formData.barId,
                    yearsOfExperience: formData.yearsOfExperience,
                    practiceAreas: formData.practiceAreas,
                    publicReferrals: formData.publicReferrals,
                    bio: formData.bio,
                    website: formData.website,
                };

                const response = await axiosInstance.post('/api/auth/signup-atomic', signupData);
                
                if (response.data.success) {
                    // Check if email confirmation is required
                    if (response.data.requiresEmailConfirmation) {
                        setErrors({ 
                            submit: 'Account created! Please check your email to verify your account before signing in.' 
                        });
                        submittedRef.current = false; // Allow retry if needed
                        setLoading(false);
                        // Optionally redirect to login after a delay
                        setTimeout(() => {
                            navigate('/signup/login');
                        }, 5000);
                        return;
                    } else {
                        // If we got a session, set it
                        if (response.data.session) {
                            await supabase.auth.setSession({
                                access_token: response.data.session.access_token,
                                refresh_token: response.data.session.refresh_token
                            });
                        }
                        navigate('/pending-approval');
                    }
                }
            }
        } catch (error) {
            // Handle specific error codes
            if (error.response?.data?.code === 'EMAIL_EXISTS') {
                setErrors({ 
                    submit: 'This email is already registered. Please sign in instead.' 
                });
            } else if (error.response?.status === 429) {
                setErrors({ 
                    submit: 'Too many signup attempts. Please try again in 15 minutes.' 
                });
            } else if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
                // Validation errors from backend
                const validationErrors = {};
                error.response.data.details.forEach(err => {
                    if (err.param === 'workEmail') validationErrors.workEmail = err.msg;
                    else validationErrors[err.param] = err.msg;
                });
                setErrors(validationErrors);
            } else {
                const errorMsg = error.response?.data?.error || error.message || 'Failed to create account.';
                setErrors({ submit: errorMsg });
            }
            
            submittedRef.current = false;
        } finally {
            setLoading(false);
        }
    };

    // Optional: Add email availability check when user types
    useEffect(() => {
        const checkEmail = async () => {
            if (formData.workEmail && formData.workEmail.includes('@') && currentPhase === 2 && !isOAuthUser) {
                try {
                    const response = await axiosInstance.get('/api/auth/check-email', {
                        params: { email: formData.workEmail.toLowerCase() }
                    });
                    
                    if (response.data.exists) {
                        setErrors(prev => ({
                            ...prev,
                            workEmail: 'This email is already registered'
                        }));
                    }
                } catch (error) {
                    // Silent fail - don't block user
                }
            }
        };
        
        const timer = setTimeout(checkEmail, 500); // Debounce
        return () => clearTimeout(timer);
    }, [formData.workEmail, currentPhase, isOAuthUser]);

    // --- RENDER FUNCTIONS (Keep as is - NO CHANGES) ---
    const renderError = () => { if (errors.submit) { return ( <div className="w-full max-w-md mb-6"> <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert"> <span className="block sm:inline">{errors.submit}</span> </div> </div> ); } return null; };
    const getPhaseTitle = () => { switch (currentPhase) { case 1: return isOAuthUser ? 'Confirm Details' : 'Create Account'; case 2: return isOAuthUser ? 'Contact Information' : 'Almost Done'; case 3: return 'Bar Verification'; default: return 'Create Account'; } };
    const getPhaseSubtitle = () => { switch (currentPhase) { case 1: return 'Personal Details'; case 2: return 'Contact Information'; case 3: return 'Bar Verification'; default: return ''; } };
    const renderContent = () => { const paddingTopClass = (currentPhase === 1 && !isOAuthUser) ? 'pt-32' : 'pt-12'; const pageContent = ( <div className={`w-full flex flex-col items-center ${paddingTopClass}`}> <h1 className="text-3xl font-bold text-[#333333] mb-6"> {getPhaseTitle()} </h1> <p className="text-gray-600 mb-6"> {getPhaseSubtitle()} </p> <div className="mb-12 w-full max-w-md"> <PhaseIndicator currentPhase={currentPhase} totalPhases={3}/> </div> {renderError()} <div className="w-full max-w-md"> {currentPhase === 1 && ( <PersonalDetails formData={formData} onChange={handleChange} errors={errors} isOAuthUser={isOAuthUser} /> )} {currentPhase === 2 && ( <FirmDetails formData={formData} onChange={handleChange} errors={errors} /> )} {currentPhase === 3 && ( <BarVerification formData={formData} onChange={handleChange} errors={errors} setFormData={setFormData} /> )} <button onClick={currentPhase === 3 ? handleSubmit : handleNext} disabled={loading} className="w-full py-3 bg-[#34261E] text-white rounded-full hover:bg-opacity-90 transition-colors mt-8 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-h-[48px]"> {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : (currentPhase === 3 ? 'Create Account & Verify' : 'Next Step')} </button> {!isOAuthUser && currentPhase === 1 && ( <div className="text-center mt-8"> <div className="text-gray-500">- or -</div> <p className="mt-4 text-gray-600"> Already have an account?{' '} <button onClick={() => navigate('/signup/login')} className="text-[#34261E] font-semibold hover:underline" > Sign in </button> </p> </div> )} </div> </div> ); if (currentPhase === 1 && !isOAuthUser) { return ( <div className="min-h-screen flex"> <div className="w-[30%] bg-[#34261E] hidden md:block" /> <div className="flex-1 bg-[#fef6ef]"> {pageContent} </div> </div> ); } return ( <div className="min-h-screen bg-[#fef6ef]"> {pageContent} </div> ); };

    // --- Main Component Return (Keep as is - NO CHANGES) ---
    return (
        <div className="relative">
            {!loading && ( <button onClick={handleBack} className={`absolute top-6 ${ currentPhase === 1 && !isOAuthUser ? 'left-6 md:left-[calc(30%+24px)]' : 'left-6' } text-gray-600 hover:text-gray-800 flex items-center gap-2 z-10 text-sm disabled:opacity-50`} > ← Back </button> )}
            {renderContent()}
        </div>
    );
};

export default RegistrationForm;